import { useEffect, useState } from 'react';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { useGetBettieProducts } from '../../../domains/bettie/ui/hooks/useGetBettieProducts';
import { openNewTab } from '../../../utils/UIUtils';
import { getOneTimePaymentLaunchPath, getPgProvider } from '../../../utils/paymentProvider';
import type { PayLetterPaymentMethod } from '../../../domains/orders/services/orderServices';
import type { components } from '../../../types/schema';

type ApiV1BettieProduct = components['schemas']['ApiV1BettieProduct'];

export const useBettieCharge = (onClose?: () => void) => {
  const { data: oneTimeProducts, isFetching: isBettieProductsLoading } = useGetBettieProducts({
    category: 'onetime',
    assetType: 'bettie_point'
  });
  
  const { data: subscriptionProducts, isFetching: isSubscriptionProductsLoading } =
    useGetBettieProducts({
      category: 'subscription',
      assetType: 'bettie_point'
    });

  const { data: user, refetch: refetchUser } = useUserQuery();

  const [selectedItem, setSelectedItem] = useState<ApiV1BettieProduct | null>(null);

  useEffect(() => {
    const nextDefaultProduct = subscriptionProducts?.[0] || oneTimeProducts?.[0] || null;
    if (nextDefaultProduct) {
      setSelectedItem(nextDefaultProduct);
    } else {
      setSelectedItem(null);
    }
  }, [subscriptionProducts, oneTimeProducts]);

  const handleSelectItem = (id: string) => {
    const allProducts = [...(subscriptionProducts || []), ...(oneTimeProducts || [])];
    const item = allProducts.find((product) => product.bettieProductId === id) ?? null;
    setSelectedItem(item);
  };

  useEffect(() => {
    refetchUser();
    const onFocus = () => {
      refetchUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchUser]);

  const handlePrimaryClick = (paymentMethod?: PayLetterPaymentMethod) => {
    if (!selectedItem || !user) return;

    const amount = Number(selectedItem.baseAmount - selectedItem.discountAmount);
    const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
    const isPayLetter = getPgProvider() === 'pay-letter';
    const encodedProductName = encodeURIComponent(selectedItem.name);

    if (selectedItem.category === 'subscription' || selectedItem.category === 'bundle') {
      if (user?.identified) {
        if (isPayLetter) {
          openNewTab(
            `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}&autoPay=true`
          );
          onClose?.();
          return false;
        }
        openNewTab(
          `/payment/billing?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}`
        );
      } else {
        openNewTab(
          `/identity-verification/intro?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}&isBilling=true${isPayLetter ? '&autoPay=true' : ''}`
        );
      }
      onClose?.();
      return false;
    }

    if (user?.identified) {
      if (isPayLetter && !paymentMethod) {
        return true;
      }
      openNewTab(
        `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}${paymentMethod ? `&paymentMethod=${paymentMethod}` : ''}`
      );
    } else {
      openNewTab(
        `/identity-verification/intro?amount=${amount}&productName=${encodedProductName}&productId=${selectedItem.bettieProductId}`
      );
    }
    onClose?.();
    return false;
  };

  return {
    subscriptionProducts: subscriptionProducts || [],
    oneTimeProducts: oneTimeProducts || [],
    selectedItem,
    handleSelectItem,
    handlePrimaryClick,
    isLoadingProducts: isBettieProductsLoading || isSubscriptionProductsLoading
  };
};

export default useBettieCharge;
